import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_CONTENT } from "@/content/defaults";
import { Logo } from "@/components/Logo";
import { ImageUploader } from "@/components/ImageUploader";

// ----- Schema describing all editable fields -----
// type: text | textarea | color | image | url
const SCHEMA = [
  {
    key: "brand", title: "Marka", fields: [
      { path: "brand.name", label: "Marka Adı (büyük)", type: "text" },
      { path: "brand.sub", label: "Alt Marka (italik)", type: "text" },
    ],
  },
  {
    key: "colors", title: "Renkler", fields: [
      { path: "colors.ivory", label: "Krem Beyaz (ana zemin)", type: "color" },
      { path: "colors.cream", label: "Açık Krem (yardımcı zemin)", type: "color" },
      { path: "colors.stone", label: "Taş Tonu", type: "color" },
      { path: "colors.gold", label: "Gold (vurgu)", type: "color" },
      { path: "colors.navy", label: "Lacivert (koyu vurgu)", type: "color" },
      { path: "colors.ink", label: "Metin (siyah)", type: "color" },
      { path: "colors.slate", label: "Metin (gri)", type: "color" },
    ],
  },
  {
    key: "contact", title: "İletişim Bilgileri", fields: [
      { path: "contact.phone_display", label: "Telefon (gösterim)", type: "text" },
      { path: "contact.whatsapp_number", label: "WhatsApp Numarası (sadece rakam, 90 ile başlayan)", type: "text" },
      { path: "contact.email", label: "E-posta", type: "text" },
      { path: "contact.address", label: "Adres", type: "text" },
      { path: "contact.instagram_handle", label: "Instagram Kullanıcı Adı", type: "text" },
      { path: "contact.instagram_url", label: "Instagram URL", type: "url" },
      { path: "contact.map_embed_src", label: "Google Maps Embed src URL", type: "textarea" },
      { path: "contact.default_whatsapp_message", label: "Varsayılan WhatsApp Mesajı", type: "textarea" },
    ],
  },
  {
    key: "navbar", title: "Üst Menü (Navbar)", fields: [
      { path: "navbar.link_home", label: "Ana Sayfa linki", type: "text" },
      { path: "navbar.link_projects", label: "Projeler linki", type: "text" },
      { path: "navbar.link_about", label: "Hakkımızda linki", type: "text" },
      { path: "navbar.link_contact", label: "İletişim linki", type: "text" },
      { path: "navbar.cta_label", label: "CTA Buton Metni", type: "text" },
    ],
  },
  {
    key: "footer", title: "Footer (Alt Bilgi)", fields: [
      { path: "footer.tagline", label: "Tagline / Alt yazı", type: "textarea" },
      { path: "footer.explore_title", label: "Keşfet başlığı", type: "text" },
      { path: "footer.contact_title", label: "İletişim başlığı", type: "text" },
      { path: "footer.address_short", label: "Kısa adres", type: "text" },
      { path: "footer.copyright", label: "Copyright metni", type: "text" },
      { path: "footer.admin_link_label", label: "Yönetim link metni", type: "text" },
    ],
  },
  {
    key: "home_hero", title: "Ana Sayfa — Hero", fields: [
      { path: "home.hero.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.hero.title_line1", label: "Başlık 1. satır", type: "text" },
      { path: "home.hero.title_italic", label: "Başlık italik kısım", type: "text" },
      { path: "home.hero.subtitle", label: "Alt yazı", type: "textarea" },
      { path: "home.hero.cta1_label", label: "Birinci buton", type: "text" },
      { path: "home.hero.cta2_label", label: "İkinci buton", type: "text" },
      { path: "home.hero.scroll_hint", label: "Kaydırma ipucu", type: "text" },
    ],
  },
  {
    key: "home_about", title: "Ana Sayfa — Biz Kimiz?", fields: [
      { path: "home.about.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.about.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "home.about.heading_italic", label: "Başlık italik kelime", type: "text" },
      { path: "home.about.heading_line2", label: "Başlık 2. parça", type: "text" },
      { path: "home.about.body", label: "Gövde metni", type: "textarea" },
      { path: "home.about.link_label", label: "Link metni", type: "text" },
      { path: "home.about.image", label: "Görsel", type: "image" },
    ],
  },
  {
    key: "home_stats", title: "Ana Sayfa — İstatistik Kartları", listPath: "home.stats", item: {
      fields: [
        { path: "number", label: "Sayı", type: "text" },
        { path: "label", label: "Etiket", type: "text" },
        { path: "note", label: "Alt not", type: "text" },
      ],
    },
  },
  {
    key: "home_projects", title: "Ana Sayfa — Projeler Bölümü", fields: [
      { path: "home.projects_section.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.projects_section.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "home.projects_section.heading_italic", label: "Başlık italik kelime", type: "text" },
      { path: "home.projects_section.view_all_label", label: "'Tüm Projeler' buton metni", type: "text" },
    ],
  },
  {
    key: "home_whyus", title: "Ana Sayfa — Neden Özkayalar?", fields: [
      { path: "home.why_us.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.why_us.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "home.why_us.heading_italic", label: "Başlık italik", type: "text" },
      { path: "home.why_us.body", label: "Gövde metni", type: "textarea" },
    ],
  },
  {
    key: "home_whyus_features", title: "Ana Sayfa — Neden Özkayalar Maddeleri", listPath: "home.why_us.features", item: {
      fields: [
        { path: "title", label: "Başlık", type: "text" },
        { path: "desc", label: "Açıklama", type: "textarea" },
      ],
    },
  },
  {
    key: "home_social", title: "Ana Sayfa — Sosyal Yaşam", fields: [
      { path: "home.social.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.social.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "home.social.heading_italic", label: "Başlık italik", type: "text" },
      { path: "home.social.body", label: "Gövde metni", type: "textarea" },
      { path: "home.social.image", label: "Görsel", type: "image" },
    ],
  },
  {
    key: "home_social_amenities", title: "Ana Sayfa — Sosyal Alan Etiketleri", listPath: "home.social.amenities", item: {
      fields: [{ path: "label", label: "Etiket", type: "text" }],
    },
  },
  {
    key: "home_final_cta", title: "Ana Sayfa — Final CTA", fields: [
      { path: "home.final_cta.eyebrow", label: "Üst etiket", type: "text" },
      { path: "home.final_cta.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "home.final_cta.heading_italic", label: "Başlık italik", type: "text" },
      { path: "home.final_cta.body", label: "Gövde metni", type: "textarea" },
      { path: "home.final_cta.cta1_label", label: "Birinci buton metni", type: "text" },
      { path: "home.final_cta.cta2_label", label: "İkinci buton metni", type: "text" },
    ],
  },
  {
    key: "projects_page", title: "Projeler Sayfası", fields: [
      { path: "projects_page.eyebrow", label: "Üst etiket", type: "text" },
      { path: "projects_page.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "projects_page.heading_italic", label: "Başlık italik", type: "text" },
      { path: "projects_page.body", label: "Gövde metni", type: "textarea" },
      { path: "projects_page.filter_all", label: "Filtre: Tümü", type: "text" },
      { path: "projects_page.filter_ongoing", label: "Filtre: Devam Eden", type: "text" },
      { path: "projects_page.filter_completed", label: "Filtre: Tamamlanan", type: "text" },
      { path: "projects_page.empty_text", label: "Boş liste metni", type: "text" },
    ],
  },
  {
    key: "about_hero", title: "Hakkımızda — Üst Bölüm", fields: [
      { path: "about_page.hero.eyebrow", label: "Üst etiket", type: "text" },
      { path: "about_page.hero.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "about_page.hero.heading_italic", label: "Başlık italik", type: "text" },
      { path: "about_page.hero.body_p1", label: "1. paragraf", type: "textarea" },
      { path: "about_page.hero.body_p2", label: "2. paragraf", type: "textarea" },
      { path: "about_page.hero.image", label: "Görsel", type: "image" },
    ],
  },
  {
    key: "about_story", title: "Hakkımızda — Kuruluş Hikayesi", fields: [
      { path: "about_page.founding_story.eyebrow", label: "Üst etiket", type: "text" },
      { path: "about_page.founding_story.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "about_page.founding_story.heading_italic", label: "Başlık italik", type: "text" },
      { path: "about_page.founding_story.body_p1", label: "1. paragraf", type: "textarea" },
      { path: "about_page.founding_story.body_p2", label: "2. paragraf", type: "textarea" },
    ],
  },
  {
    key: "about_pillars", title: "Hakkımızda — 4 Sütun (Misyon vb.)", listPath: "about_page.pillars", item: {
      fields: [
        { path: "title", label: "Başlık", type: "text" },
        { path: "text", label: "Metin", type: "textarea" },
      ],
    },
  },
  {
    key: "about_awards", title: "Hakkımızda — Alt İstatistik Kartları", listPath: "about_page.award_stats", item: {
      fields: [
        { path: "number", label: "Sayı", type: "text" },
        { path: "label", label: "Etiket", type: "text" },
      ],
    },
  },
  {
    key: "contact_page", title: "İletişim Sayfası", fields: [
      { path: "contact_page.eyebrow", label: "Üst etiket", type: "text" },
      { path: "contact_page.heading_line1", label: "Başlık 1. parça", type: "text" },
      { path: "contact_page.heading_italic", label: "Başlık italik", type: "text" },
      { path: "contact_page.body", label: "Gövde", type: "textarea" },
      { path: "contact_page.form_title", label: "Form başlığı", type: "text" },
      { path: "contact_page.form_subtitle", label: "Form alt yazısı", type: "text" },
      { path: "contact_page.label_name", label: "Ad Soyad etiketi", type: "text" },
      { path: "contact_page.label_phone", label: "Telefon etiketi", type: "text" },
      { path: "contact_page.label_project", label: "Proje seçim etiketi", type: "text" },
      { path: "contact_page.label_message", label: "Mesaj etiketi", type: "text" },
      { path: "contact_page.submit_label", label: "Gönder buton metni", type: "text" },
      { path: "contact_page.submit_note", label: "Form alt notu", type: "text" },
      { path: "contact_page.sidebar_eyebrow", label: "Sidebar üst etiket", type: "text" },
      { path: "contact_page.sidebar_heading", label: "Sidebar başlık", type: "text" },
    ],
  },
  {
    key: "project_detail", title: "Proje Detay Sayfası", fields: [
      { path: "project_detail.back_label", label: "Geri butonu", type: "text" },
      { path: "project_detail.info_cta_label", label: "Bilgi Al butonu", type: "text" },
      { path: "project_detail.contact_cta_label", label: "İletişim Formu butonu", type: "text" },
      { path: "project_detail.about_eyebrow", label: "Hakkında üst etiket", type: "text" },
      { path: "project_detail.about_heading_line1", label: "Hakkında başlık 1. parça", type: "text" },
      { path: "project_detail.about_heading_italic", label: "Hakkında italik", type: "text" },
      { path: "project_detail.features_title", label: "Özellikler kutusu başlığı", type: "text" },
      { path: "project_detail.social_eyebrow", label: "Sosyal alanlar üst etiket", type: "text" },
      { path: "project_detail.social_heading", label: "Sosyal alanlar başlığı", type: "text" },
      { path: "project_detail.location_eyebrow", label: "Konum üst etiket", type: "text" },
      { path: "project_detail.location_heading", label: "Konum başlığı", type: "text" },
      { path: "project_detail.gallery_eyebrow", label: "Galeri üst etiket", type: "text" },
      { path: "project_detail.gallery_heading_line1", label: "Galeri başlık 1. parça", type: "text" },
      { path: "project_detail.gallery_heading_italic", label: "Galeri italik", type: "text" },
      { path: "project_detail.cta_eyebrow", label: "Alt CTA üst etiket", type: "text" },
      { path: "project_detail.cta_heading_italic", label: "Alt CTA italik", type: "text" },
      { path: "project_detail.cta_body", label: "Alt CTA gövdesi", type: "textarea" },
      { path: "project_detail.cta_whatsapp_label", label: "Alt CTA WhatsApp butonu", type: "text" },
      { path: "project_detail.cta_contact_label", label: "Alt CTA İletişim butonu", type: "text" },
    ],
  },
];

// ----- Helpers -----
const getByPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);

const setByPath = (obj, path, value) => {
  const keys = path.split(".");
  const out = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = cur[k] && typeof cur[k] === "object" ? (Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] }) : {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
};

// ----- Field renderer -----
const Field = ({ field, value, onChange }) => {
  const common = "w-full bg-transparent border border-[var(--navy)]/20 focus:border-[var(--gold)] outline-none p-2.5 font-light text-sm transition-colors";
  switch (field.type) {
    case "textarea":
      return <textarea rows="3" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
    case "color":
      return (
        <div className="flex gap-3 items-center">
          <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 border border-[var(--navy)]/20 cursor-pointer" />
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${common} font-mono`} placeholder="#000000" />
        </div>
      );
    case "image":
      return (
        <ImageUploader
          label=""
          value={value ? [value] : []}
          onChange={(arr) => onChange(arr[0] || "")}
          multiple={false}
          testIdPrefix={`img-${field.path}`}
        />
      );
    default:
      return <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
  }
};

// ----- Section: object fields -----
const ObjectSection = ({ section, draft, setDraft }) => (
  <div className="space-y-4">
    {section.fields.map((f) => (
      <div key={f.path}>
        <label className="block text-xs tracking-[0.16em] uppercase text-[var(--navy)] mb-2">{f.label}</label>
        <Field field={f} value={getByPath(draft, f.path)} onChange={(v) => setDraft(setByPath(draft, f.path, v))} />
      </div>
    ))}
  </div>
);

// ----- Section: list of items -----
const ListSection = ({ section, draft, setDraft }) => {
  const list = getByPath(draft, section.listPath) || [];
  return (
    <div className="space-y-6">
      {list.map((item, idx) => (
        <div key={idx} className="border border-[var(--gold)]/25 p-4">
          <div className="text-xs tracking-[0.18em] uppercase text-[var(--slate)] mb-3">Madde #{idx + 1}</div>
          <div className="space-y-3">
            {section.item.fields.map((f) => (
              <div key={f.path}>
                <label className="block text-xs tracking-[0.16em] uppercase text-[var(--navy)] mb-2">{f.label}</label>
                <Field
                  field={f}
                  value={item[f.path]}
                  onChange={(v) => {
                    const newList = [...list];
                    newList[idx] = { ...newList[idx], [f.path]: v };
                    setDraft(setByPath(draft, section.listPath, newList));
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ----- Main component -----
export default function AdminContent() {
  const { user, loading: authLoading } = useAuth();
  const { content, refresh } = useSiteContent();
  const [draft, setDraft] = useState(content);
  const [openKey, setOpenKey] = useState("brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(content); }, [content]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-[var(--slate)]">Yükleniyor…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/site-content", draft);
      await refresh();
      toast.success("İçerik kaydedildi — site güncellendi");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const onResetAll = () => {
    if (!window.confirm("Tüm değişiklikler varsayılana dönecek. Emin misiniz?")) return;
    setDraft(DEFAULT_CONTENT);
    toast.message("Varsayılana sıfırlandı (kaydet'e basana kadar yayınlanmaz)");
  };

  return (
    <div data-testid="admin-content-page" className="min-h-screen bg-[var(--ivory)]">
      <header className="border-b border-[var(--gold)]/25 bg-[var(--cream)] px-6 lg:px-12 py-5 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-6">
          <Logo variant="light" size="md" />
          <span className="hidden md:inline text-xs tracking-[0.22em] uppercase text-[var(--slate)]">Site İçeriği</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-xs tracking-[0.18em] uppercase text-[var(--navy)] hover:text-[var(--gold)] inline-flex items-center gap-1" data-testid="content-back-projects">
            <ArrowLeft size={14} /> Projeler
          </Link>
          <button onClick={onResetAll} className="btn-ghost-gold !py-2 !px-4" data-testid="content-reset">
            <RotateCcw size={14} /> Sıfırla
          </button>
          <button onClick={onSave} disabled={saving} className="btn-primary !py-2.5 !px-5" data-testid="content-save">
            <Save size={14} /> {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="font-serif-luxe text-4xl text-[var(--navy)]">Site İçeriği Yönetimi</h1>
          <p className="text-sm text-[var(--slate)] font-light mt-2">
            Tüm sayfaların metinlerini, görsellerini ve renklerini buradan düzenleyebilirsiniz. Değişiklikleri kaydetmek için sağ üstteki <b>Kaydet</b> butonunu kullanın.
          </p>
        </div>

        <div className="space-y-3">
          {SCHEMA.map((section) => {
            const open = openKey === section.key;
            return (
              <div key={section.key} className="lux-card overflow-hidden" data-testid={`content-section-${section.key}`}>
                <button
                  onClick={() => setOpenKey(open ? null : section.key)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[var(--cream)]/60 transition-colors"
                  data-testid={`content-toggle-${section.key}`}
                >
                  <span className="font-serif-luxe text-xl text-[var(--navy)]">{section.title}</span>
                  {open ? <ChevronDown size={18} className="text-[var(--gold)]" /> : <ChevronRight size={18} className="text-[var(--navy)]/50" />}
                </button>
                {open && (
                  <div className="px-6 pb-6 border-t border-[var(--gold)]/15 pt-6">
                    {section.listPath ? <ListSection section={section} draft={draft} setDraft={setDraft} /> : <ObjectSection section={section} draft={draft} setDraft={setDraft} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-6 mt-10 flex justify-end">
          <button onClick={onSave} disabled={saving} className="btn-primary shadow-[0_18px_50px_rgba(10,25,47,0.25)]" data-testid="content-save-bottom">
            <Save size={16} /> {saving ? "Kaydediliyor…" : "Tüm Değişiklikleri Kaydet"}
          </button>
        </div>
      </main>
    </div>
  );
}
